import React, { useState } from "react";

interface ShowcaseItem {
  title: string;
  subtitle: string;
  items: string[];
  signals: string[];
  accentClass: string;
}

const showcaseData: ShowcaseItem[] = [
  {
    title: "Architecture",
    subtitle: "System shape and service boundaries",
    accentClass: "text-sky-300",
    signals: ["Scalable", "Observable", "Reliable"],
    items: [
      "Microservices",
      "Serverless",
      "Event-Driven",
      "GraphQL Federation",
      "Clean Architecture",
      "DDD",
    ],
  },
  {
    title: "APIs",
    subtitle: "Contract design and runtime performance",
    accentClass: "text-amber-300",
    signals: ["Versioned", "Secure", "Low-latency"],
    items: [
      "REST APIs",
      "GraphQL",
      "WebSocket",
      "gRPC",
      "API Gateway",
      "Rate Limiting",
    ],
  },
  {
    title: "Databases",
    subtitle: "Storage engines and data consistency",
    accentClass: "text-emerald-300",
    signals: ["Indexed", "Replicated", "Durable"],
    items: [
      "PostgreSQL",
      "MongoDB",
      "ClickHouse",
      "DynamoDB",
      "Redis Cache",
      "MySQL",
    ],
  },
  {
    title: "Tools",
    subtitle: "AI-assisted engineering and delivery workflow",
    accentClass: "text-rose-300",
    signals: ["AI-Augmented", "Containerized", "Production-Ready"],
    items: [
      "Claude Code",
      "OpenAI Codex",
      "Google Gemini",
      "GitHub Copilot",
      "Cursor IDE",
      "Prisma ORM",
      "Drizzle ORM",
      "Docker",
      "AWS",
      "CI/CD Pipelines",
      "Kibana",
      "Postman",
    ],
  },
];

const BackendShowcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState(3);
  const active = showcaseData[activeTab];

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.14),transparent_45%),radial-gradient(circle_at_bottom_left,rgba(251,191,36,0.1),transparent_40%),linear-gradient(to_bottom,rgba(15,23,42,0.95),rgba(15,23,42,0.7))] p-5 shadow-[0_24px_90px_rgba(0,0,0,0.25)] md:p-7">
        <div
          className="absolute -left-16 -top-16 h-40 w-40 rounded-full bg-cyan-300/10 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-16 -right-10 h-44 w-44 rounded-full bg-amber-300/10 blur-3xl"
          aria-hidden="true"
        />

        <div className="relative z-10 mb-7 md:mb-8">
          <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-cyan-200/90 mb-2">
            Systems Toolkit
          </p>
          <h3 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-white">
            Backend depth, surfaced as an operator dashboard.
          </h3>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-300">
            Explore architecture, APIs, data, and delivery capabilities in a
            cleaner panel that feels like runtime telemetry rather than a static
            list.
          </p>
        </div>

        <div className="relative z-10 mb-6 grid grid-cols-2 gap-2.5 md:grid-cols-4">
          {showcaseData.map((item, index) => (
            <button
              key={item.title}
              onClick={() => setActiveTab(index)}
              className={`rounded-xl border px-3 py-3 text-left transition-all duration-300 md:px-4 ${
                activeTab === index
                  ? "border-cyan-200/40 bg-cyan-200/10"
                  : "border-white/15 bg-white/[0.03] hover:border-white/30 hover:bg-white/[0.05]"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300">
                  0{index + 1}
                </span>
                <span
                  className={`h-2 w-2 rounded-full ${
                    activeTab === index ? "bg-cyan-300" : "bg-slate-500"
                  }`}
                />
              </div>
              <p className="mt-2 text-sm font-semibold text-white">
                {item.title}
              </p>
              <p className="mt-1 text-xs text-slate-400">{item.subtitle}</p>
            </button>
          ))}
        </div>

        <div className="relative z-10 rounded-2xl border border-white/15 bg-slate-950/40 p-4 md:p-5">
          <div className="mb-4 flex flex-wrap items-center gap-2.5 border-b border-white/10 pb-4">
            <span className="font-mono text-xs uppercase tracking-[0.14em] text-slate-300">
              Active Domain
            </span>
            <span className="rounded-full border border-white/15 bg-white/[0.03] px-2.5 py-1 text-xs text-white">
              {active.title}
            </span>
            {active.signals.map((signal) => (
              <span
                key={signal}
                className="rounded-full border border-cyan-200/20 bg-cyan-200/10 px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.12em] text-cyan-100"
              >
                {signal}
              </span>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-[0.85fr_1.15fr] md:items-start">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                Domain Notes
              </p>
              <p
                className={`mt-2 text-xl font-semibold tracking-tight ${active.accentClass}`}
              >
                {active.title}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                {active.title === "Tools"
                  ? "This lane highlights the AI coding assistants and delivery tooling I use to ship real product features quickly and cleanly."
                  : `${active.subtitle}. This lane groups the tools and patterns I ship with most often in production environments.`}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-4">
              {active.items.map((skill) => (
                <div
                  key={skill}
                  className="rounded-lg border border-white/12 bg-gradient-to-br from-white/[0.09] to-white/[0.02] px-3 py-3 text-sm text-slate-100"
                >
                  {skill}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackendShowcase;
